import type {CommandType} from '@github-ui/copilot-shared-ui/ahp/command-types'
import type {
  AskUserArguments,
  ExitPlanModeArguments,
  InteractivePromptToolName,
  PermissionRequestArguments,
} from '@github-ui/copilot-shared-ui/ahp/interactive-prompt-types'
// Import the derived type from constants
import type {KnownToolName} from '../constants/tool-names'
import type {CopilotAnnotation} from './code-insights-metadata'
import type {AbortMessage} from './session-events'
import type {ReasoningEffort} from '@github-ui/copilot-shared-ui/utils/reasoning-effort'
import type {ToolLogMetadataBag} from './tool-log-metadata'
import type {UserMessageImage} from './user-message-image'

export type MessageRole = 'user' | 'assistant' | 'tool'

export type Delta = {
  content?: string
  /** Inline image previews carried by a tool result. */
  images?: UserMessageImage[]
  assistant_content?: string
  role?: MessageRole
  reasoning_text?: string
  tool_calls?: Array<{
    function?: {
      arguments?: string
      name: ToolName
      result?: string
    }
    custom?: {
      input?: string
      name: ToolName
    }
    id: string
    type: string
    index: number
  }>
  copilot_annotations?: CopilotAnnotation | string
}

export type StreamingMessage = {
  type?: string
  id: string
  created: number
  model: string
  object: string
  choices: Choice[]
}

export type Choice = {
  finish_reason: string
  delta: Delta
}

export type CompletionChunk = {
  id: string
  choices: Choice[]
}

export type CommandMessage = CompletionUserMessage & {source: string}

export type CompletionUserMessage = {
  role: 'user'
  content: string | Array<{type: 'text'; text: string} | {type: 'image_url' | 'input_audio' | 'file'}>
  source: string
  created?: number
}

export type CompletionToolMessage = {
  role: 'tool'
  tool_call_id: string
  content: string | Array<{type: 'text'; text: string}>
}

export type ChatMessage = {
  id: string
  threadID: string
  role: 'user' | 'assistant'
  content?: string
  choices?: Choice[]
}

export type ToolUserRequestedMessage = {
  type: 'tool.user_requested'
  toolCallId: string
  toolName: InteractivePromptToolName
  arguments: AskUserArguments | ExitPlanModeArguments | PermissionRequestArguments
  timestamp: string
}

export type ToolExecutionStartMessage = {
  type: 'tool.execution_start'
  id: string
  timestamp: string
  data: {
    toolCallId: string
    toolName: string
    arguments: unknown
    parentToolCallId?: string
  }
}

// tool.outputs event indicates a tool call (including permission prompts) has completed
export type ToolOutputsMessage = {
  type: 'tool.outputs'
  toolCallId: string
  output: string
  timestamp: string
}

// session.idle signals that the agent's agentic loop has completed and it's idle
export type SessionIdleMessage = {
  type: 'session.idle'
  id: string
  timestamp: string
  agentId?: string
}

// assistant.turn_start signals the beginning of a new assistant turn
export type AssistantTurnStartMessage = {
  type: 'assistant.turn_start'
  id: string
  timestamp: string
  data: {
    turnId: string
  }
}

// assistant.intent carries the agent's current working status text
export type AssistantIntentMessage = {
  type: 'assistant.intent'
  id: string
  timestamp: string
  data: {
    intent: string
  }
}

// session.mode_changed signals that the CLI agent mode has changed
export type LogEntry =
  | CompletionChunk
  | CompletionUserMessage
  | CompletionToolMessage
  | ToolUserRequestedMessage
  | ToolExecutionStartMessage
  | ToolOutputsMessage
  | SessionIdleMessage
  | AbortMessage
  | AssistantTurnStartMessage
  | AssistantIntentMessage
  | Record<string, unknown>

/**
 * Preprocessed log entry - deduplicated and normalized for rendering.
 */
export type PreprocessedLogEntry =
  | CompletionChunk
  | CompletionToolMessage
  | ChatMessage
  | Record<string, unknown>
  | CompletionUserMessage[]

/**
 * Maps tool_call_id to its associated metadata bag.
 */
export type ToolCallMetadataMap = Record<string, ToolLogMetadataBag>

/**
 * Hydrated log entry - preprocessed entry with tool-call-scoped metadata attached.
 */
export type HydratedLogEntry = PreprocessedLogEntry & {
  tool_call_metadata?: ToolCallMetadataMap
}

export function isCompletionChunk(logEntry: HydratedLogEntry): logEntry is CompletionChunk {
  if (typeof logEntry !== 'object' || logEntry === null) {
    return false
  }

  return 'choices' in logEntry && Array.isArray(logEntry.choices)
}

export function isCommandGroup(logEntry: HydratedLogEntry): logEntry is CompletionUserMessage[] {
  return Array.isArray(logEntry) && logEntry.every(entry => isCommandMessage(entry))
}

export function isCommandMessage(logEntry: LogEntry): logEntry is CommandMessage {
  return isCompletionUserMessage(logEntry) && 'source' in logEntry
}

export function isCompletionUserMessage(logEntry: LogEntry): logEntry is CompletionUserMessage {
  if (typeof logEntry !== 'object' || logEntry === null) {
    return false
  }
  return 'role' in logEntry && logEntry.role === 'user'
}

export function isCompletionToolMessage(logEntry: LogEntry): logEntry is CompletionToolMessage {
  if (typeof logEntry !== 'object' || logEntry === null) {
    return false
  }
  return 'role' in logEntry && logEntry.role === 'tool'
}

export function isChatMessage(logEntry?: LogEntry): logEntry is ChatMessage {
  if (typeof logEntry !== 'object' || logEntry === null || logEntry === undefined) {
    return false
  }
  return 'role' in logEntry && (logEntry.role === 'user' || logEntry.role === 'assistant') && 'threadID' in logEntry
}

export function isToolUserRequestedMessage(logEntry: LogEntry): logEntry is ToolUserRequestedMessage {
  if (typeof logEntry !== 'object' || logEntry === null) {
    return false
  }

  if (!('type' in logEntry) || logEntry.type !== 'tool.user_requested') {
    return false
  }

  if (!('toolCallId' in logEntry) || typeof logEntry.toolCallId !== 'string') {
    return false
  }

  if (!('toolName' in logEntry) || typeof logEntry.toolName !== 'string') {
    return false
  }

  if (!('arguments' in logEntry) || typeof logEntry.arguments !== 'object') {
    return false
  }

  if (!('timestamp' in logEntry) || typeof logEntry.timestamp !== 'string') {
    return false
  }

  return true
}

export function isToolExecutionStartMessage(logEntry: LogEntry): logEntry is ToolExecutionStartMessage {
  if (typeof logEntry !== 'object' || logEntry === null) {
    return false
  }

  if (!('type' in logEntry) || logEntry.type !== 'tool.execution_start') {
    return false
  }

  if (!('id' in logEntry) || typeof logEntry.id !== 'string') {
    return false
  }

  if (!('timestamp' in logEntry) || typeof logEntry.timestamp !== 'string') {
    return false
  }

  if (!('data' in logEntry) || typeof logEntry.data !== 'object' || logEntry.data === null) {
    return false
  }

  const data = logEntry.data as Record<string, unknown>
  if (typeof data.toolCallId !== 'string' || typeof data.toolName !== 'string') {
    return false
  }

  return true
}

export function isToolOutputsMessage(logEntry: LogEntry): logEntry is ToolOutputsMessage {
  if (typeof logEntry !== 'object' || logEntry === null) {
    return false
  }

  if (!('type' in logEntry) || logEntry.type !== 'tool.outputs') {
    return false
  }

  if (!('toolCallId' in logEntry) || typeof logEntry.toolCallId !== 'string') {
    return false
  }

  if (!('output' in logEntry) || typeof logEntry.output !== 'string') {
    return false
  }

  if (!('timestamp' in logEntry) || typeof logEntry.timestamp !== 'string') {
    return false
  }

  return true
}

export function isSessionIdleMessage(logEntry: LogEntry): logEntry is SessionIdleMessage {
  return typeof logEntry === 'object' && logEntry !== null && 'type' in logEntry && logEntry.type === 'session.idle'
}

export function isAssistantTurnStartMessage(logEntry: LogEntry): logEntry is AssistantTurnStartMessage {
  return (
    typeof logEntry === 'object' &&
    logEntry !== null &&
    'type' in logEntry &&
    logEntry.type === 'assistant.turn_start' &&
    'data' in logEntry &&
    typeof (logEntry as AssistantTurnStartMessage).data?.turnId === 'string'
  )
}

export function isAssistantIntentMessage(logEntry: LogEntry): logEntry is AssistantIntentMessage {
  return (
    typeof logEntry === 'object' &&
    logEntry !== null &&
    'type' in logEntry &&
    logEntry.type === 'assistant.intent' &&
    'data' in logEntry &&
    typeof (logEntry as AssistantIntentMessage).data?.intent === 'string'
  )
}

export function isAskUserPrompt(message: ToolUserRequestedMessage): boolean {
  return message.toolName === 'ask_user'
}

export function isExitPlanModePrompt(message: ToolUserRequestedMessage): boolean {
  return message.toolName === 'exit_plan_mode'
}

export function isPermissionRequestPrompt(message: ToolUserRequestedMessage): boolean {
  return message.toolName === 'permission_request'
}

/**
 * Type guard to validate AskUserArguments structure at runtime.
 * Accepts both canonical (`question`) and CLI (`message` + `requestedSchema`) formats.
 */
export function isAskUserArguments(args: unknown): args is AskUserArguments {
  if (typeof args !== 'object' || args === null) return false
  const obj = args as Record<string, unknown>

  // Elicitation format: message + structurally valid requestedSchema
  if (typeof obj.message === 'string' && obj.requestedSchema && typeof obj.requestedSchema === 'object') {
    const schema = obj.requestedSchema as Record<string, unknown>
    if (schema.properties && typeof schema.properties === 'object' && Object.keys(schema.properties).length > 0) {
      return true
    }
  }

  // Simple format: must have question
  if (typeof obj.question === 'string') {
    if (obj.choices !== undefined && !Array.isArray(obj.choices)) return false
    if (obj.allowFreeform !== undefined && typeof obj.allowFreeform !== 'boolean') return false
    return true
  }

  return false
}

/**
 * Type guard to validate ExitPlanModeArguments structure at runtime.
 */
export function isExitPlanModeArguments(args: unknown): args is ExitPlanModeArguments {
  if (typeof args !== 'object' || args === null) return false
  const obj = args as Record<string, unknown>
  if (typeof obj.summary !== 'string') return false
  if (obj.actions !== undefined && !Array.isArray(obj.actions)) return false
  if (obj.recommendedAction !== undefined && typeof obj.recommendedAction !== 'string') return false
  return true
}

/**
 * Type guard to validate PermissionRequestArguments structure at runtime.
 */
export function isPermissionRequestArguments(args: unknown): args is PermissionRequestArguments {
  if (typeof args !== 'object' || args === null) return false
  const obj = args as Record<string, unknown>
  if (typeof obj.kind !== 'string') return false
  if (obj.toolName !== undefined && typeof obj.toolName !== 'string') return false
  if (obj.serverName !== undefined && typeof obj.serverName !== 'string') return false
  if (obj.command !== undefined && typeof obj.command !== 'string') return false
  if (obj.filePath !== undefined && typeof obj.filePath !== 'string') return false
  if (obj.diff !== undefined && typeof obj.diff !== 'string') return false
  if (obj.description !== undefined && typeof obj.description !== 'string') return false
  if (obj.canOfferSessionApproval !== undefined && typeof obj.canOfferSessionApproval !== 'boolean') return false
  if (obj.commandIdentifiers !== undefined && !Array.isArray(obj.commandIdentifiers)) return false
  if (obj.args !== undefined && (typeof obj.args !== 'object' || obj.args === null || Array.isArray(obj.args)))
    return false
  if (obj.toolArgs !== undefined && typeof obj.toolArgs !== 'string') return false
  return true
}

export function isValidLogEntry(message: unknown): message is LogEntry {
  if (typeof message !== 'object' || message === null) {
    return false
  }

  return (
    isCompletionChunk(message as LogEntry) ||
    isCompletionUserMessage(message as LogEntry) ||
    isCompletionToolMessage(message as LogEntry) ||
    isToolUserRequestedMessage(message as LogEntry) ||
    isToolExecutionStartMessage(message as LogEntry) ||
    typeof message === 'object'
  )
}

export const ConnectionStatus = {
  Connected: 'connected',
  Disconnected: 'disconnected',
} as const

export type ConnectionStatus = (typeof ConnectionStatus)[keyof typeof ConnectionStatus]

export type Session = {
  id: string
  name: string
  user_id: number
  user_login?: string
  agent_id: number
  agent_slug?: string
  state: SessionState
  owner_id: number
  repo_id: number
  task_id?: string
  resource_type: string
  resource_id: number
  resource_global_id: string
  created_at: string // ISO 8601 string
  /** @deprecated Use updated_at when copilot_task_api_github_rest_style is enabled */
  last_updated_at?: string // ISO 8601 string
  /** New field name used when copilot_task_api_github_rest_style is enabled */
  updated_at?: string // ISO 8601 string
  completed_at?: string // Optional ISO 8601 string,
  workflow_run_id: number
  log_entries: LogEntry[]
  error: {
    message: string
  } | null
  premium_requests: number
  usage?: {
    type: 'ai_credits' | 'premium_requests' | 'pru'
    amount?: number
    credits?: number
  }
  event_identifiers: string[] | null
  event_type: string | null
  event_url: string | null
  event_content: string | null
  isPending?: boolean
  base_ref?: string
  model?: string
  reasoning_effort?: ReasoningEffort
  remote_steerable?: boolean
  compute?: TaskCompute
  /**
   * Identifier of the AHP environment this session is bound to. Set at session
   * creation and immutable thereafter; present when the session runs on an
   * environment registered via the Mission Control Environments API.
   */
  environment_id?: string
}

export type TaskCompute = {
  provider?: string
  scope?: string
}

/**
 * Session states shared by all providers (sweagentd, CLI).
 */
export const SessionState = {
  // Common states (all providers)
  Queued: 'queued',
  InProgress: 'in_progress',
  Completed: 'completed',
  Failed: 'failed',
  TimedOut: 'timed_out',
  Cancelled: 'cancelled',

  // CLI-only states
  Idle: 'idle',
  WaitingForUser: 'waiting_for_user',
} as const

export type SessionState = (typeof SessionState)[keyof typeof SessionState]

const SESSION_STATES_SET = new Set<SessionState>(Object.values(SessionState))

/**
 * Session states that indicate the session has ended.
 * When a session reaches one of these states, the server has acknowledged the termination.
 */
export const TERMINAL_SESSION_STATES: ReadonlySet<SessionState> = new Set([
  SessionState.Completed,
  SessionState.Failed,
  SessionState.TimedOut,
  SessionState.Cancelled,
])

export function isSessionState(state: string): state is SessionState {
  return SESSION_STATES_SET.has(state as SessionState)
}

export function getReadableSessionState(state: SessionState, options?: {hasPull?: boolean}) {
  switch (state) {
    case SessionState.InProgress:
      return 'In progress'
    case SessionState.Completed:
      return options?.hasPull ? 'Ready for review' : 'Completed'
    case SessionState.Failed:
      return 'Failed'
    case SessionState.Idle:
      return 'Idle'
    case SessionState.WaitingForUser:
      return 'Needs attention'
    case SessionState.TimedOut:
      return 'Timed out'
    case SessionState.Cancelled:
      return 'Cancelled'
    case SessionState.Queued:
      return 'Queued'
    default:
      return 'Unknown'
  }
}

/**
 * A session is "active" if it can still accept and process commands.
 */
export function isActiveSession(session: Session) {
  return ['queued', 'in_progress', 'idle', 'waiting_for_user'].includes(session.state)
}

export function isQueuedSession(session: Session) {
  return session.state === SessionState.Queued
}

export function isCompletedSession(session: Session) {
  return session.state === SessionState.Completed
}

export function isPendingSession(session: Session) {
  return !!session.isPending && session.state === SessionState.Queued
}

export function isCancelledSession(session: Session) {
  return session.state === SessionState.Cancelled
}

export function isFailedSession(session: Session) {
  return session.state === SessionState.Failed
}

/**
 * Tool names that can be used by the agent.
 * Includes all known tools (from tool-names.ts) plus any custom MCP tool.
 */
export type ToolName = KnownToolName | string

export type BashToolArgs = {
  command: string
  description?: string
}

export type ReadBashToolArgs = {
  shellId: string
  /** @deprecated Use shellId. Kept for backwards compat with old sessions. */
  sessionId?: string
}

export type WriteBashToolArgs = {
  shellId: string
  /** @deprecated Use shellId. Kept for backwards compat with old sessions. */
  sessionId?: string
  input: string
}

export type StopBashToolArgs = {
  shellId: string
  /** @deprecated Use shellId. Kept for backwards compat with old sessions. */
  sessionId?: string
}

export type AsyncBashToolArgs = {
  command: string
  shellId?: string
  /** @deprecated Use shellId. Kept for backwards compat with old sessions. */
  sessionId?: string
}

export type ReadAsyncBashToolArgs = {
  shellId?: string
  /** @deprecated Use shellId. Kept for backwards compat with old sessions. */
  sessionId?: string
}

export type StopAsyncBashToolArgs = {
  shellId?: string
  /** @deprecated Use shellId. Kept for backwards compat with old sessions. */
  sessionId?: string
}

export type ListBashToolArgs = Record<string, unknown>

export type PowerShellToolArgs = {
  command: string
  description?: string
}

export type ReadPowerShellToolArgs = {
  shellId: string
  /** @deprecated Use shellId. Kept for backwards compat with old sessions. */
  sessionId?: string
}

export type WritePowerShellToolArgs = {
  shellId: string
  /** @deprecated Use shellId. Kept for backwards compat with old sessions. */
  sessionId?: string
  input: string
}

export type StopPowerShellToolArgs = {
  shellId: string
  /** @deprecated Use shellId. Kept for backwards compat with old sessions. */
  sessionId?: string
}

export type ListPowerShellToolArgs = Record<string, unknown>

export type ThinkToolArgs = {
  thought: string
}

export type RunCustomSetupStepToolArgs = {
  name: string
}

export type CodeReviewToolArgs = {
  prTitle: string
  prDescription: string
}

export type SetupMcpDetails = {
  type: 'mcp_setup'
  /**
   * May not be present if setup failed.
   */
  toolNamesToDisplayNames?: Record<string, string>
}

export type SetupDetails = SetupMcpDetails

export type RunSetupArgs = {
  name: string
  details?: SetupDetails
}

export type StrReplaceEditorToolArgs = {
  /**
   * Will be `undefined` when the agent is using discrete editing tools.
   */
  command?: 'view' | 'create' | 'str_replace' | 'insert' | 'undo_edit'
  path: string
  view_range?: [number, number]
  insert_line?: number
}

export type ReportProgressToolArgs =
  | {
      prDescription: string
      commitMessage: string | undefined
    }
  | {
      /**
       * A markdown checklist of the plan as tasks and progress on the tasks completed so far.
       */
      tasksChecklist: string
      /**
       * The next incomplete task to be worked on from the checklist.
       */
      nextTask: string
    }

export type CommitChangesToolArgs = {
  commitMessage: string | undefined
}

export type ReplyToCommentToolArgs = {
  comment_id: string
  reply: string
}

export type GitHubMCPServerWebSearchToolArgs = {
  query: string
}

export type GitHubMCPServerPullRequestReadToolArgs = {
  method:
    | 'get'
    | 'get_diff'
    | 'get_status'
    | 'get_files'
    | 'get_reviews'
    | 'get_review_comments'
    | 'get_comments'
    | string
  owner: string
  repo: string
  pullNumber: number
}

export type WebFetchToolArgs = {
  url: string
}

export type ProposeWorkToolArgs = {
  workTitle: string
  workDescription: string
  workType: 'code_change' | 'task'
}

export type GrepToolArgs = {
  pattern: string
  path?: string
  output_mode?: string
  glob?: string
  type?: string
  '-i'?: boolean
  '-A'?: number
  '-B'?: number
  '-C'?: number
  '-n'?: boolean
  head_limit?: number
  multiline?: boolean
}

export type GlobToolArgs = {
  pattern: string
  path?: string
}

export type SearchCodeSubagentToolArgs = {
  query: string
}

export type SkillToolArgs = {
  skill: string
}

export type TaskToolArgs = {
  description: string
  prompt: string
  agent_type: string
  model?: string
  mode?: 'sync' | 'background'
}

export type CreatePullRequestToolArgs = {
  title: string
  description: string
  draft: boolean
}

export type TaskCompleteToolArgs = {
  summary: string
}

export type ReadAgentToolArgs = {
  agent_id: string
  wait?: boolean
  timeout?: number
  since_turn?: number
}

export type WriteAgentToolArgs = {
  agent_id: string
  message?: string
}

export type ListAgentsToolArgs = {
  include_completed?: boolean
}

export type ToolArgs =
  | BashToolArgs
  | ThinkToolArgs
  | StrReplaceEditorToolArgs
  | ReportProgressToolArgs
  | ReplyToCommentToolArgs
  | AsyncBashToolArgs
  | ReadAsyncBashToolArgs
  | StopAsyncBashToolArgs
  | ReadBashToolArgs
  | WriteBashToolArgs
  | StopBashToolArgs
  | ListBashToolArgs
  | PowerShellToolArgs
  | ReadPowerShellToolArgs
  | WritePowerShellToolArgs
  | StopPowerShellToolArgs
  | ListPowerShellToolArgs
  | RunCustomSetupStepToolArgs
  | GitHubMCPServerWebSearchToolArgs
  | GitHubMCPServerPullRequestReadToolArgs
  | WebFetchToolArgs
  | CodeReviewToolArgs
  | ProposeWorkToolArgs
  | GrepToolArgs
  | GlobToolArgs
  | SearchCodeSubagentToolArgs
  | SkillToolArgs
  | TaskToolArgs
  | CreatePullRequestToolArgs
  | TaskCompleteToolArgs
  | ReadAgentToolArgs
  | WriteAgentToolArgs
  | ListAgentsToolArgs
  | Record<string, unknown>

export type Command = {
  id: string
  external_id: string | null
  user_id: number
  session_id: string
  content: string
  /**
   * Optional short, human-readable summary of `content`. When present, this is
   * what should be shown to the user instead of `content`, which may be a long
   * machine-facing prompt (e.g. Agent Merge steering updates).
   */
  display_content?: string | null
  created_at: string
  updated_at: string
  state: 'queued' | 'in_progress' | 'completed' | 'failed' | 'dismissed'
  type?: CommandType
  error: string | null
}

export type UserCommandState = Command['state']

export type CommandsResponse = {
  commands: Command[]
}

export type SessionIdToLogsMap = Record<Session['id'], LogEntry[]>
